import * as THREE from 'three';
import { PITCH } from '../../core/math';
import { StaticBuilder } from './util';
import { grassDetailTexture, netTexture, patchTexture } from './textures';

/** Extent of the grass plane (reaches the stand front walls). */
export const GROUND = { halfX: 61, halfZ: 42 } as const;
/** Pitch-side LED board lines. */
export const BOARDS = { sideZ: 39, endX: 57.5, dugoutGap: 14 } as const;
export const DUGOUT = { x: 8, z: -38.6, len: 8, depth: 2.2 } as const;

export interface PitchUniforms {
  uWet: { value: number };
  uSnow: { value: number };
  uGrassLight: { value: THREE.Color };
  uGrassDark: { value: THREE.Color };
}

const PITCH_GLSL_FUNCS = /* glsl */`
uniform sampler2D uDetail;
uniform sampler2D uPatch;
uniform vec3 uGrassLight;
uniform vec3 uGrassDark;
uniform vec3 uTrack;
uniform float uWet;
uniform float uSnow;
varying vec2 vPitch;

float sdSeg(vec2 p, vec2 a, vec2 b) { vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float sdBox2(vec2 p, vec2 b) { vec2 d = abs(p) - b; return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0); }

const float HW = 0.06; // 12 cm markings
float markingDist(vec2 p) {
  float d = abs(sdBox2(p, vec2(${PITCH.halfL.toFixed(2)}, ${PITCH.halfW.toFixed(2)})));
  d = min(d, sdSeg(p, vec2(0.0, -${PITCH.halfW.toFixed(2)}), vec2(0.0, ${PITCH.halfW.toFixed(2)})));
  d = min(d, abs(length(p) - 9.15));
  d = min(d, length(p) - 0.12 + HW);
  vec2 q = vec2(abs(p.x), p.y);
  float bx = ${(PITCH.halfL - PITCH.boxDepth).toFixed(2)};
  float bz = ${(PITCH.boxWidth / 2).toFixed(2)};
  d = min(d, sdSeg(q, vec2(bx, -bz), vec2(bx, bz)));
  d = min(d, sdSeg(q, vec2(bx, -bz), vec2(52.5, -bz)));
  d = min(d, sdSeg(q, vec2(bx, bz), vec2(52.5, bz)));
  d = min(d, sdSeg(q, vec2(47.0, -9.16), vec2(47.0, 9.16)));
  d = min(d, sdSeg(q, vec2(47.0, -9.16), vec2(52.5, -9.16)));
  d = min(d, sdSeg(q, vec2(47.0, 9.16), vec2(52.5, 9.16)));
  vec2 spot = vec2(41.5, 0.0);
  d = min(d, length(q - spot) - 0.12 + HW);
  if (q.x < bx) d = min(d, abs(length(q - spot) - 9.15));
  vec2 c = abs(p);
  if (c.x < 52.5 && c.y < 34.0) d = min(d, abs(length(c - vec2(52.5, 34.0)) - 1.0));
  // technical areas in front of the dugouts (near side)
  vec2 t = vec2(abs(p.x) - ${DUGOUT.x.toFixed(1)}, p.y + 36.6);
  if (p.y < -34.5) d = min(d, abs(sdBox2(t, vec2(5.5, 2.1))));
  return d;
}
`;

export function buildPitch(): { mesh: THREE.Mesh; uniforms: PitchUniforms; material: THREE.MeshStandardMaterial } {
  const geo = new THREE.PlaneGeometry(GROUND.halfX * 2, GROUND.halfZ * 2, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const detail = grassDetailTexture();
  const patch = patchTexture();
  const uniforms = {
    uDetail: { value: detail },
    uPatch: { value: patch },
    uGrassLight: { value: new THREE.Color('#4f9a34') },
    uGrassDark: { value: new THREE.Color('#2f7424') },
    uTrack: { value: new THREE.Color('#2a3a2a') },
    uWet: { value: 0 },
    uSnow: { value: 0 },
  };
  const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.92, metalness: 0 });
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vPitch;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvPitch = position.xz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + PITCH_GLSL_FUNCS)
      .replace('#include <map_fragment>', /* glsl */`
        vec2 P = vPitch;
        // mowing stripes: 18 bands along the length + faint cross-cut
        float sw = ${(PITCH.length / 18).toFixed(4)};
        float tri = abs(fract((P.x + 52.5) / (2.0 * sw)) * 2.0 - 1.0);
        float aa = fwidth(tri) * 1.2 + 0.004;
        float stripe = smoothstep(0.5 - aa, 0.5 + aa, tri);
        float triZ = abs(fract((P.y + 34.0) / 14.0) * 2.0 - 1.0);
        float crossCut = smoothstep(0.5 - 0.02, 0.5 + 0.02, triZ);
        vec3 grass = mix(uGrassDark, uGrassLight, stripe);
        grass *= 0.96 + 0.06 * crossCut;
        float det = texture2D(uDetail, P / 1.6).r;
        float det2 = texture2D(uDetail, P / 7.3 + 0.37).r;
        float patchv = texture2D(uPatch, P / 55.0).r;
        grass *= (0.78 + 0.42 * det) * (0.9 + 0.2 * det2) * (0.75 + 0.5 * patchv);
        // close-range blade texture: a fine layer that fades out before it can shimmer
        float fp = length(fwidth(P));
        float fineW = 1.0 - smoothstep(0.012, 0.05, fp);
        if (fineW > 0.0) {
          float det3 = texture2D(uDetail, P / 0.31 + 0.71).r;
          float det4 = texture2D(uDetail, P.yx / 0.13 + 0.23).r;
          grass *= mix(1.0, (0.82 + 0.3 * det3) * (0.9 + 0.18 * det4), fineW);
        }
        // subtle large-scale hue drift (no two halves of the pitch quite the same green)
        grass *= vec3(1.0 + 0.05 * (patchv - 0.5), 1.0, 1.0 - 0.06 * (patchv - 0.5));
        // wear: keeper's area (elongated along the goal line), penalty spots, centre spot,
        // and the linesmen's running tracks just outside the touchlines
        vec2 gq = vec2((abs(P.x) - 51.2) * 1.25, P.y * 0.55);
        float wear = exp(-dot(gq, gq) / 7.0) * 0.6;
        vec2 ps = vec2(abs(P.x) - 41.5, P.y);
        wear += exp(-dot(ps, ps) / 1.6) * 0.3;
        wear += exp(-dot(P, P) / 5.0) * 0.2;
        float track = (1.0 - smoothstep(0.35, 1.1, abs(abs(P.y) - 34.9))) * step(0.0, P.y * sign(P.x) * -1.0 + 0.0) * (1.0 - smoothstep(48.0, 53.0, abs(P.x)));
        wear += track * 0.22 * smoothstep(0.3, 0.8, det2);
        wear *= 0.55 + 0.9 * det;
        grass = mix(grass, vec3(0.20, 0.17, 0.08), clamp(wear, 0.0, 0.7));
        // beyond the boards: synthetic surround
        float outside = step(${BOARDS.sideZ.toFixed(1)} + 0.4, abs(P.y)) + step(${BOARDS.endX.toFixed(1)} + 0.4, abs(P.x));
        grass = mix(grass, uTrack * (0.85 + 0.3 * det), clamp(outside, 0.0, 1.0));
        // wet & snow
        grass *= mix(1.0, 0.72, uWet);
        float snowN = smoothstep(0.35, 0.75, det2 * 0.6 + patchv * 0.7);
        grass = mix(grass, vec3(0.55, 0.58, 0.62), uSnow * (0.06 + 0.42 * snowN));
        // markings (analytic, AA, never thinner than ~1px but faded when sub-pixel)
        float md = markingDist(P);
        float px = fwidth(md) * 0.8 + 1e-4;
        float hw = max(HW, px);
        float lineA = (1.0 - smoothstep(hw - px, hw + px, md)) * (HW / hw) * (1.0 - clamp(outside, 0.0, 1.0));
        vec3 lineCol = vec3(0.86, 0.88, 0.86) * (0.92 + 0.08 * det);
        diffuseColor.rgb = mix(grass, lineCol, lineA);
      `)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.32, uWet);');
  };
  mat.customProgramCacheKey = () => 'sd-pitch-v2';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'pitch';
  mesh.receiveShadow = true;
  mesh.matrixAutoUpdate = false;
  return { mesh, uniforms, material: mat };
}

/** Goal frames, corner flags, dugout shells into the static builder; nets as their own mesh. */
export function buildGoals(sb: StaticBuilder): THREE.Mesh {
  const gw = PITCH.goalWidth / 2, gh = PITCH.goalHeight, r = 0.06;
  const white = '#f4f4f4';
  for (const s of [-1, 1]) {
    const x = s * (PITCH.halfL + r);
    sb.cylinder(r, r, gh, x, gh / 2, -gw - r, white, 10);
    sb.cylinder(r, r, gh, x, gh / 2, gw + r, white, 10);
    sb.cylinder(r, r, gw * 2 + r * 4, x, gh + r, 0, white, 10, Math.PI / 2);
    // net support stanchions (dark, behind)
    const back = x + s * 2.2;
    sb.cylinder(0.025, 0.025, 2.3, x + s * 1.0, 1.15, -gw - 0.15, '#3a3a3a', 6);
    sb.cylinder(0.025, 0.025, 2.3, x + s * 1.0, 1.15, gw + 0.15, '#3a3a3a', 6);
    sb.cylinder(0.02, 0.02, gw * 2 + 0.3, back, 0.02, 0, '#3a3a3a', 6, Math.PI / 2);
  }
  // corner flags
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const x = sx * PITCH.halfL, z = sz * PITCH.halfW;
    sb.cylinder(0.018, 0.018, 1.55, x, 0.775, z, '#f2f2f2', 6);
    const t = 1.55, b = 1.22, fx = x + sx * 0.42;
    for (const flip of [false, true]) {
      const tri = (a: number[], bb: number[], c: number[]) => (flip ? [...a, ...c, ...bb] : [...a, ...bb, ...c]);
      sb.tris(tri([x, t, z], [fx, t, z], [x, b, z]), '#ffd400');
      sb.tris(tri([fx, t, z], [fx, b, z], [x, b, z]), '#e01b24');
    }
  }
  // nets
  const pos: number[] = [], uv: number[] = [];
  const cell = 0.12;
  const quad = (a: number[], b: number[], c: number[], d: number[], ua: number[], ub: number[], uc: number[], ud: number[]) => {
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
    uv.push(...ua, ...ub, ...uc, ...ua, ...uc, ...ud);
  };
  for (const s of [-1, 1]) {
    const x0 = s * (PITCH.halfL + 0.12);
    const W = gw + 0.12;
    const P = [[0, gh + 0.02], [1.0, gh - 0.1], [2.2, 0.0]] as const;
    // top + back strips across z
    let along = 0;
    for (let i = 0; i < 2; i++) {
      const [d0, h0] = P[i]!; const [d1, h1] = P[i + 1]!;
      const segLen = Math.hypot(d1 - d0, h1 - h0);
      const xa = x0 + s * d0, xb = x0 + s * d1;
      quad([xa, h0, -W], [xa, h0, W], [xb, h1, W], [xb, h1, -W],
        [0, along / cell], [2 * W / cell, along / cell], [2 * W / cell, (along + segLen) / cell], [0, (along + segLen) / cell]);
      along += segLen;
    }
    // sides
    for (const z of [-W, W]) {
      const pts = [[0, gh + 0.02], [1.0, gh - 0.1], [2.2, 0], [0, 0]] as const;
      const v = (i: number) => [x0 + s * pts[i]![0], pts[i]![1], z];
      const u = (i: number) => [pts[i]![0] / cell, pts[i]![1] / cell];
      quad(v(0), v(1), v(2), v(3), u(0), u(1), u(2), u(3));
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  const mat = new THREE.MeshLambertMaterial({ map: netTexture(), transparent: true, side: THREE.DoubleSide, depthWrite: false, color: 0xf2f2f2, opacity: 0.75 });
  const mesh = new THREE.Mesh(g, mat);
  mesh.name = 'nets';
  mesh.renderOrder = 2;
  mesh.matrixAutoUpdate = false;
  return mesh;
}
